import React from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { escPosToHtml } from '@/app/utils/escPosToHtml'
import { VoucherResponse } from '@/app/types/voucher'
import { Party } from '@/app/types/party'
import { CompanyDetails } from '@/app/services/thermalPrinterService'
import { ThermalReceipt } from './ThermalReceipt'
import { thermalReceiptToHtml } from '@/app/utils/thermalReceiptToHtml'
import { PartyClosingBalanceResponse } from '@/app/types/reports'

interface ThermalPrintPreviewProps {
  isOpen: boolean
  onClose: () => void
  receiptHTML?: string
  receiptData?: Uint8Array
  isEscPos?: boolean
  paperSize?: '80mm' | '58mm' | '44mm'
  onPrint?: () => void
  // New props for react-thermal-printer
  invoice?: VoucherResponse
  party?: Party
  company?: CompanyDetails
  charactersPerLine?: number
  closingBalance?: PartyClosingBalanceResponse
}

export const ThermalPrintPreview: React.FC<ThermalPrintPreviewProps> = ({
  isOpen,
  onClose,
  receiptHTML,
  receiptData,
  isEscPos = false,
  paperSize = '80mm',
  onPrint,
  invoice,
  party,
  company,
  charactersPerLine,
  closingBalance
}) => {
  const widthMap = {
    '80mm': 300,
    '58mm': 220,
    '44mm': 165
  }

  const paperWidth = widthMap[paperSize] || 300
  
  // Add print styles when component mounts
  React.useEffect(() => {
    if (isOpen) {
      const style = document.createElement('style')
      style.innerHTML = `
        @media print {
          /* Hide everything except the receipt */
          body * {
            visibility: hidden;
          }
          .thermal-receipt-preview, .thermal-receipt-preview * {
            visibility: visible;
          }
          .thermal-receipt-preview {
            position: absolute;
            left: 0;
            top: 0;
            width: 80mm !important;
            max-width: 80mm !important;
            font-family: 'Courier New', Courier, monospace !important;
            font-size: 9px !important;
            line-height: 1.2 !important;
            font-weight: 400 !important;
            letter-spacing: 0.01em !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            background: white !important;
            padding: 10px !important;
            margin: 0 !important;
          }
          /* Ensure bold text prints properly */
          .thermal-receipt-preview strong {
            font-weight: bold !important;
          }
          /* Fix for transform scale in print */
          .thermal-receipt-preview span[style*="transform"] {
            transform: none !important;
            font-size: 14px !important;
            font-weight: bold !important;
          }
          @page {
            size: 80mm auto;
            margin: 0;
          }
        }
      `
      document.head.appendChild(style)
      
      return () => {
        document.head.removeChild(style)
      }
    }
  }, [isOpen])

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-fit p-0">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle>Thermal Receipt Preview</DialogTitle>
        </DialogHeader>
        
        <div className="p-4 pt-0">
          <div className="mb-3 text-sm text-muted-foreground">
            Paper Size: {paperSize} | Mode: {isEscPos ? 'ESC/POS' : 'Text'}
          </div>
          
          {/* Receipt Preview Container */}
          <div 
            className="mx-auto bg-white shadow-lg overflow-auto"
            style={{
              width: `${paperWidth}px`,
              maxHeight: '600px',
              border: '1px solid #e0e0e0',
              backgroundColor: '#f9f9f9'
            }}
          >
            {invoice && company ? (
              <div
                className="thermal-receipt-preview"
                style={{
                  padding: '10px 8px',
                  fontFamily: '"Courier New", Courier, monospace',
                  fontSize: '10px',
                  lineHeight: '1.2',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  margin: 0,
                  color: '#000',
                  backgroundColor: '#fff',
                  letterSpacing: '-0.5px',
                  fontWeight: 400,
                  // Ensure content doesn't overflow
                  overflow: 'hidden',
                  boxSizing: 'border-box'
                }}
                dangerouslySetInnerHTML={{
                  __html: thermalReceiptToHtml(invoice, party, company, paperSize, charactersPerLine, closingBalance)
                }}
              />
            ) : (
              <div
                className="thermal-receipt-preview"
                style={{
                  padding: '10px 8px',
                  fontFamily: '"Courier New", Courier, monospace',
                  fontSize: '10px',
                  lineHeight: '1.2',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  margin: 0,
                  color: '#000',
                  backgroundColor: '#fff',
                  letterSpacing: '-0.5px',
                  fontWeight: 400,
                  // Ensure content doesn't overflow
                  overflow: 'hidden',
                  boxSizing: 'border-box'
                }}
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    if (isEscPos && receiptData) {
                      return escPosToHtml(receiptData)
                    } else {
                      return (receiptHTML || '')
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n/g, '<br/>')
                    }
                  })()
                }}
              />
            )}
          </div>
          
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              onClick={() => {
                if (onPrint) {
                  // Use the actual thermal print function
                  onPrint()
                } else {
                  // Fallback to window.print
                  window.print()
                }
              }}
            >
              Print
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                // Print to browser/PDF for testing
                window.print()
              }}
            >
              Print to PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}